/**
 * Small, best-effort offline store for email UI data.
 *
 * This deliberately stores message data only — never OAuth tokens or other
 * credentials. Every key is scoped by the signed-in user so a later session
 * cannot render another user's cached mail during the first paint.
 */

export type EmailCacheStore = 'lists' | 'messages' | 'threads' | 'sharedGroups' | 'sharedThreads';

interface EmailCacheRecord<T> {
  key: string;
  value: T;
  savedAt: number;
}

const DB_NAME = 'quantis-email-cache-v1';
const DB_VERSION = 1;
const STORES: EmailCacheStore[] = ['lists', 'messages', 'threads', 'sharedGroups', 'sharedThreads'];
const STORE_LIMITS: Record<EmailCacheStore, number> = {
  lists: 24,
  messages: 200,
  threads: 100,
  sharedGroups: 16,
  sharedThreads: 160,
};
const trimInFlight = new Set<string>();
// Moves on sign-out, so data read before it cannot land after the stores are emptied.
let generation = 0;

/** Captured before a network read, and handed to its write, so a reply that lands after sign-out is dropped. */
export function emailCacheGeneration(): number {
  return generation;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDatabase(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        for (const store of STORES) {
          if (!db.objectStoreNames.contains(store)) db.createObjectStore(store, { keyPath: 'key' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

/** Reads a cached value without ever making the caller wait on failures. */
export async function readEmailCache<T>(store: EmailCacheStore, key: string): Promise<T | null> {
  try {
    const db = await openDatabase();
    if (!db) return null;
    return await new Promise<T | null>((resolve) => {
      const request = db.transaction(store, 'readonly').objectStore(store).get(key);
      request.onsuccess = () => resolve((request.result as EmailCacheRecord<T> | undefined)?.value ?? null);
      request.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

/** Writes are intentionally fire-and-forget from UI call sites. */
export async function writeEmailCache<T>(store: EmailCacheStore, key: string, value: T, started = generation): Promise<void> {
  try {
    const db = await openDatabase();
    if (!db || generation !== started) return;
    await new Promise<void>((resolve) => {
      const request = db
        .transaction(store, 'readwrite')
        .objectStore(store)
        .put({ key, value, savedAt: Date.now() } satisfies EmailCacheRecord<T>);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
    });
    queueTrim(store, key.split('::', 1)[0]);
  } catch {
    // Offline persistence must never turn a usable mail screen into an error.
  }
}

function queueTrim(store: EmailCacheStore, userKey: string): void {
  const trimKey = `${store}::${userKey}`;
  if (trimInFlight.has(trimKey) || typeof setTimeout === 'undefined') return;
  trimInFlight.add(trimKey);
  setTimeout(() => {
    void trimStore(store, userKey).finally(() => trimInFlight.delete(trimKey));
  }, 0);
}

async function trimStore(store: EmailCacheStore, userKey: string): Promise<void> {
  const db = await openDatabase();
  if (!db) return;
  const records = await new Promise<Array<EmailCacheRecord<unknown>>>((resolve) => {
    const request = db.transaction(store, 'readonly').objectStore(store).getAll();
    request.onsuccess = () => resolve((request.result as Array<EmailCacheRecord<unknown>>).filter(
      (record) => record.key.startsWith(`${userKey}::`),
    ));
    request.onerror = () => resolve([]);
  });
  const excess = records
    .sort((a, b) => b.savedAt - a.savedAt)
    .slice(STORE_LIMITS[store]);
  if (excess.length === 0) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(store, 'readwrite');
    const objectStore = tx.objectStore(store);
    for (const record of excess) objectStore.delete(record.key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
    tx.onabort = () => resolve();
  });
}

/** Empties every store when this device signs out, whoever was signed in. */
export async function clearEmailCache(): Promise<void> {
  generation++;
  try {
    const db = await openDatabase();
    if (!db) return;
    await Promise.all(STORES.map((store) => new Promise<void>((resolve) => {
      const request = db.transaction(store, 'readwrite').objectStore(store).clear();
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
    })));
  } catch {
    // Best effort only; the user can still sign out safely.
  }
}

if (typeof window !== 'undefined') window.addEventListener('quantis:auth-cleared', () => { void clearEmailCache(); });

/** Removes all persisted records for one user when they sign out. */
export async function clearEmailCacheForUser(userId: string): Promise<void> {
  try {
    const db = await openDatabase();
    if (!db) return;
    await Promise.all(STORES.map((store) => new Promise<void>((resolve) => {
      const request = db.transaction(store, 'readwrite').objectStore(store).openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return resolve();
        if (String(cursor.key).startsWith(`${userId}::`)) cursor.delete();
        cursor.continue();
      };
      request.onerror = () => resolve();
    })));
  } catch {
    // Best effort only; the user can still sign out safely.
  }
}
